/**
 * services/ai/AIProviderFactory.ts
 * AI provider factory function
 *
 * Creates the correct AIProvider implementation for a given provider type.
 * Simple dispatch — no state, no caching, no side effects.
 *
 * Work Unit: WU04 - AI Provider Client Implementations
 * Task: T03 - Provider Factory & Manager
 * Sprint: S01 - AI Architecture & Infrastructure Foundation
 * Project: P09 - AI Integration
 */

// Internal absolute imports (type-only)
import type { AIProvider, AIProviderType } from '@/types/shared/AI';

// Sibling imports (value)
import { AnthropicProvider } from './AnthropicProvider';
import { OpenAIProvider } from './OpenAIProvider';

/**
 * Create an AI provider instance for the given provider type
 *
 * Returns the correct `AIProvider` implementation initialized with
 * the provided API key. The caller is responsible for key management —
 * this function performs no validation or storage.
 *
 * @param provider - Which AI provider to create
 * @param apiKey - Plaintext API key for the provider
 * @returns Configured AIProvider instance
 *
 * @example
 * ```ts
 * const provider = createProvider('openai', 'sk-...');
 * const response = await provider.chat([{ role: 'user', content: 'Hello' }]);
 * ```
 */
export function createProvider(provider: AIProviderType, apiKey: string): AIProvider {
  switch (provider) {
    case 'anthropic':
      return new AnthropicProvider(apiKey);
    case 'openai':
      return new OpenAIProvider(apiKey);
  }
}
